// src/pages/FleetImportGuide.tsx
import React, { useState } from 'react';
import { Rocket, Upload, AlertCircle, CheckCircle, ExternalLink, Star } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import api from '../api/axios';
import styles from '../styles/FleetImportGuide.module.css';

interface ImportResult {
  count: number;
  matched: number;
  unmatched: number;
  unmatchedShips: Array<{ shipName: string; manufacturer: string }>;
}

const FleetImportGuide: React.FC = () => {
  const navigate = useNavigate();
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const [importResult, setImportResult] = useState<ImportResult | null>(null);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file && file.type === 'application/json') {
      setUploadFile(file);
      setUploadError('');
    } else {
      setUploadError('Please select a valid JSON file');
    }
  };

  const handleUpload = async () => {
    if (!uploadFile) return;

    try {
      setUploading(true);
      setUploadError('');
      setImportResult(null);
      
      const fileContent = await uploadFile.text();
      const fleetData = JSON.parse(fileContent);

      if (!Array.isArray(fleetData)) {
        throw new Error('JSON must be an array of ships');
      }

      const response = await api.post('/userfleet/import', fleetData);
      
      setUploadSuccess(true);
      setImportResult(response.data);
      
      setTimeout(() => {
        navigate('/my-fleet');
      }, 4000);
    } catch (error: any) {
      console.error('Error uploading fleet:', error);
      setUploadError(error.response?.data?.message || error.message || 'Failed to upload fleet');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h1 className={styles.title}>
          <Rocket className={styles.titleIcon} /> Fleet Import Guide
        </h1>
        <p className={styles.subtitle}>Import your Star Citizen fleet from external tools</p>
      </div>

      {/* Upload Section */}
      <div className={styles.uploadSection}>
        <div className={styles.uploadBox}>
          <Upload size={48} className={styles.uploadIcon} />
          <h3>Quick Upload</h3>
          <p>Have a JSON file ready? Upload it now!</p>
          
          <input
            type="file"
            accept=".json"
            onChange={handleFileChange}
            id="fileInput"
            className={styles.fileInput}
          />
          
          <label htmlFor="fileInput" className={styles.fileLabel}>
            {uploadFile ? uploadFile.name : 'Choose JSON File'}
          </label>

          {uploadFile && !uploadSuccess && (
            <button 
              onClick={handleUpload} 
              disabled={uploading}
              className={styles.uploadButton}
            >
              {uploading ? 'Uploading...' : 'Upload Fleet'}
            </button>
          )}

          {uploadSuccess && importResult && (
            <div className={styles.successMessage}>
              <CheckCircle size={20} />
              <div>
                <strong>Fleet uploaded successfully!</strong>
                <div className={styles.importStats}>
                  <div>Total ships: {importResult.count}</div>
                  <div>✓ Matched: {importResult.matched}</div>
                  {importResult.unmatched > 0 && (
                    <div className={styles.unmatchedWarning}>
                      ⚠ Unmatched: {importResult.unmatched}
                    </div>
                  )}
                </div>
                {importResult.unmatched > 0 && importResult.unmatchedShips.length > 0 && (
                  <details className={styles.unmatchedDetails}>
                    <summary>View unmatched ships</summary>
                    <ul>
                      {importResult.unmatchedShips.map((ship, idx) => (
                        <li key={idx}>{ship.shipName} ({ship.manufacturer})</li>
                      ))}
                    </ul>
                  </details>
                )}
                <div style={{ marginTop: '0.5rem' }}>Redirecting to fleet...</div>
              </div>
            </div>
          )}

          {uploadError && (
            <div className={styles.errorMessage}>
              {uploadError}
            </div>
          )}
        </div>
      </div>

      {/* Recommended Tool Section */}
      <div className={styles.recommendedTool}>
        <div className={styles.toolHeader}>
          <Star size={32} className={styles.starIcon} />
          <div>
            <h2>Recommended: HangarXPLOR Browser Extension</h2>
            <span className={styles.badge}>Best Option</span>
          </div>
        </div>
        <p className={styles.toolDesc}>
          <strong>HangarXPLOR</strong> is a powerful browser extension that automatically extracts your RSI hangar data with complete accuracy, including package information, LTI status, pledge costs, and acquisition dates.
        </p>
        <div className={styles.toolFeatures}>
          <div className={styles.feature}>✓ Automatic RSI hangar extraction</div>
          <div className={styles.feature}>✓ Package grouping & tracking</div>
          <div className={styles.feature}>✓ LTI and pledge date capture</div>
          <div className={styles.feature}>✓ One-click JSON export</div>
        </div>
        <div className={styles.toolLinks}>
          <a 
            href="https://chrome.google.com/webstore/search/HangarXPLOR" 
            target="_blank" 
            rel="noopener noreferrer"
            className={styles.externalLink}
          >
            <ExternalLink size={16} />
            Get for Chrome
          </a>
          <a 
            href="https://addons.mozilla.org/firefox/" 
            target="_blank" 
            rel="noopener noreferrer"
            className={styles.externalLink}
          >
            <ExternalLink size={16} />
            Get for Firefox
          </a>
        </div>
        <div className={styles.creditNote}>
          <AlertCircle size={16} />
          Special thanks to the HangarXPLOR team for creating this excellent tool for the Star Citizen community!
        </div>
      </div>

      {/* Method Cards */}
      <div className={styles.methodsGrid}>
        {/* Manual JSON Creation */}
        <div className={styles.methodCard}>
          <div className={styles.methodHeader}>
            <Upload size={32} className={styles.methodIcon} />
            <h2>Manual JSON Creation</h2>
            <span className={styles.badge}>Advanced</span>
          </div>
          <p className={styles.methodDesc}>
            Create your own JSON file manually following our format specification
          </p>

          <div className={styles.tipBox}>
            <h4>📋 Required vs Optional Fields</h4>
            <div className={styles.fieldTable}>
              <div className={styles.fieldRow}>
                <strong>shipName</strong> <span className={styles.required}>required</span>
                <p>Full ship name (e.g., "Anvil Hornet F7C", "Aegis Sabre")</p>
              </div>
              <div className={styles.fieldRow}>
                <strong>manufacturer</strong> <span className={styles.required}>required</span>
                <p>Manufacturer name (e.g., "Anvil Aerospace", "Aegis Dynamics")</p>
              </div>
              <div className={styles.fieldRow}>
                <strong>variant</strong> <span className={styles.optional}>optional</span>
                <p>Variant code (e.g., "F7C", "Comet")</p>
              </div>
              <div className={styles.fieldRow}>
                <strong>role</strong> <span className={styles.optional}>optional</span>
                <p>Ship role (e.g., "Fighter", "Cargo", "Exploration")</p>
              </div>
              <div className={styles.fieldRow}>
                <strong>status</strong> <span className={styles.optional}>optional</span>
                <p>Ownership status: "owned", "pledged", "loaned", "concept"</p>
              </div>
              <div className={styles.fieldRow}>
                <strong>acquisitionMethod</strong> <span className={styles.optional}>optional</span>
                <p>How acquired: "pledge", "in-game", "gift", "upgrade"</p>
              </div>
              <div className={styles.fieldRow}>
                <strong>value</strong> <span className={styles.optional}>optional</span>
                <p>Pledge value in USD (number only, e.g., 70.00)</p>
              </div>
              <div className={styles.fieldRow}>
                <strong>notes</strong> <span className={styles.optional}>optional</span>
                <p>Any additional notes (e.g., "LTI | From: Exploration Pack")</p>
              </div>
            </div>
          </div>

          <div className={styles.tipBox}>
            <h4>📝 Format Examples</h4>
            
            <h5>Minimum Example (Required Fields Only)</h5>
            <div className={styles.jsonExample}>
              <pre>{`[
  {
    "shipName": "Anvil Hornet F7C",
    "manufacturer": "Anvil Aerospace"
  }
]`}</pre>
            </div>

            <h5>Complete Example (All Fields)</h5>
            <div className={styles.jsonExample}>
              <pre>{`[
  {
    "shipName": "Anvil Hornet F7C",
    "manufacturer": "Anvil Aerospace",
    "variant": "F7C",
    "role": "Fighter",
    "status": "owned",
    "acquisitionMethod": "pledge",
    "value": 110.00,
    "notes": "LTI | From: Squadron 42 Pack"
  }
]`}</pre>
            </div>

            <h5>Multi-Ship Example</h5>
            <div className={styles.jsonExample}>
              <pre>{`[
  {
    "shipName": "Anvil Carrack",
    "manufacturer": "Anvil Aerospace",
    "role": "Exploration",
    "status": "pledged",
    "value": 600.00,
    "notes": "LTI"
  },
  {
    "shipName": "Origin 325a",
    "manufacturer": "Origin Jumpworks",
    "variant": "325a",
    "role": "Fighter",
    "status": "owned"
  },
  {
    "shipName": "Drake Cutlass Black",
    "manufacturer": "Drake Interplanetary",
    "role": "Multi-role",
    "acquisitionMethod": "in-game"
  }
]`}</pre>
            </div>
          </div>

          <div className={styles.tipBox}>
            <h4>⚠️ Common Mistakes to Avoid</h4>
            <ul className={styles.mistakeList}>
              <li><strong>Missing quotes</strong> - All text values must be in "quotes"</li>
              <li><strong>Missing commas</strong> - Separate objects and fields with commas</li>
              <li><strong>Trailing commas</strong> - Don't put comma after last item</li>
              <li><strong>Invalid JSON</strong> - Use a JSON validator before uploading</li>
              <li><strong>Wrong ship names</strong> - Use exact names for best matching</li>
              <li><strong>Currency symbols in value</strong> - Use numbers only (70.00, not "$70.00")</li>
            </ul>
          </div>

          <div className={styles.tipBox}>
            <h4>✅ Best Practices</h4>
            <ul className={styles.bestPractices}>
              <li>Use exact ship names from the official website when possible</li>
              <li>Include manufacturer for better matching accuracy</li>
              <li>Validate your JSON with an online validator before uploading</li>
              <li>Use consistent date formats (YYYY-MM-DD recommended)</li>
              <li>Keep notes concise but informative</li>
              <li>Group package ships using the notes field (e.g., "From: Pack Name")</li>
            </ul>
          </div>

          <div className={styles.tipBox}>
            <h4>🔗 HangarXPLOR Format Support</h4>
            <p>TFIC also automatically recognizes the HangarXPLOR extension format:</p>
            <div className={styles.jsonExample}>
              <pre>{`[
  {
    "name": "325a Fighter",
    "ship_name": "325a",
    "manufacturer_name": "Origin Jumpworks",
    "ship_code": "ORIG_325a",
    "pledge_cost": "$70.00 USD",
    "pledge_date": "October 14, 2020",
    "pledge_id": "12345",
    "pledge_name": "Exploration Mega Pack",
    "lti": true
  }
]`}</pre>
            </div>
            <p style={{ marginTop: '0.5rem', fontSize: '0.9rem', fontStyle: 'italic' }}>
              This format is automatically detected and converted - no manual adjustments needed!
            </p>
          </div>
        </div>

        {/* Alternative: Manual Entry */}
        <div className={styles.methodCard}>
          <div className={styles.methodHeader}>
            <AlertCircle size={32} className={styles.methodIcon} />
            <h2>Alternative: Manual Entry</h2>
            <span className={styles.badge}>Full Control</span>
          </div>
          <p className={styles.methodDesc}>
            Create your fleet entry by entry directly in the TFIC interface
          </p>

          <div className={styles.steps}>
            <div className={styles.step}>
              <div className={styles.stepNumber}>1</div>
              <div className={styles.stepContent}>
                <h3>Go to My Fleet</h3>
                <p>Navigate to the <strong>My Fleet</strong> page from the sidebar</p>
              </div>
            </div>

            <div className={styles.step}>
              <div className={styles.stepNumber}>2</div>
              <div className={styles.stepContent}>
                <h3>Add Ships Manually</h3>
                <p>Use the interface to add ships one by one with all their details</p>
                <p className={styles.hint}>
                  This gives you complete control over each ship's information
                </p>
              </div>
            </div>

            <div className={styles.step}>
              <div className={styles.stepNumber}>3</div>
              <div className={styles.stepContent}>
                <h3>Track Your Fleet</h3>
                <p>View statistics, filter by manufacturer, and manage your collection</p>
              </div>
            </div>
          </div>

          <div className={styles.tipBox}>
            <h4>💡 Best For</h4>
            <p>Manual entry is ideal if you:</p>
            <ul>
              <li>Have a small fleet</li>
              <li>Want precise control over ship details</li>
              <li>Can't find a compatible export tool</li>
              <li>Prefer entering data yourself</li>
            </ul>
          </div>
        </div>
      </div>

      <div className={styles.supportNote}>
        <AlertCircle size={20} />
        <div>
          <strong>Need Help?</strong>
          <p>If you're having trouble importing your fleet, contact support or use the manual entry method.</p>
        </div>
      </div>
    </div>
  );
};

export default FleetImportGuide;
